import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useModal, useConfirm } from "../../hooks/useAdmin";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";
import { Card } from "../shared/Card";
import { Badge } from "../shared/Badge";
import { Loading } from "../shared/Loading";
import { ErrorMessage } from "../shared/ErrorMessage";
import { Modal } from "../shared/Modal";
import { EmptyState } from "../shared/EmptyState";
import * as adminApi from "../../services/api";

const DIAS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const normalizeHora = (h) => {
  // DRF a veces devuelve "HH:MM:SS"
  if (!h) return "";
  const parts = String(h).split(":");
  if (parts.length >= 2) return `${pad2(parts[0])}:${pad2(parts[1])}`;
  return h;
};

export function PlantillasSection({ primaryColor = "#ea580c" }) {
  const formModal = useModal();
  const generarModal = useModal();
  const { confirmAsync } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [plantillas, setPlantillas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // form plantilla
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    tipo: "",
    cupos: "10",
    activa: true,
  });
  const [formErrors, setFormErrors] = useState({});

  // horarios (bulk replace)
  const [horarios, setHorarios] = useState([]);
  const [nuevoHorario, setNuevoHorario] = useState({ dia_semana: 0, hora: "" });
  const [horariosLoading, setHorariosLoading] = useState(false);

  // generar
  const [genData, setGenData] = useState({ semanas: "4", limpiar: false });
  const [genErrors, setGenErrors] = useState({});

  const reload = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await adminApi.listarPlantillasAdmin();
      if (data?.error) throw new Error(data.error);
      setPlantillas(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Error cargando plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plantillasFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return plantillas;
    const term = searchTerm.toLowerCase();
    return plantillas.filter((p) => {
      const nombre = (p.nombre || "").toLowerCase();
      const tipo = (p.tipo || "").toLowerCase();
      const cupos = (p.cupos ?? "").toString();
      const activa = p.activa ? "activa" : "inactiva";
      const desc = (p.descripcion_horarios || "").toLowerCase();
      return (
        nombre.includes(term) ||
        tipo.includes(term) ||
        cupos.includes(term) ||
        activa.includes(term) ||
        desc.includes(term)
      );
    });
  }, [plantillas, searchTerm]);

  const resultsLabel = useMemo(() => {
    if (!searchTerm.trim()) return `Total: ${plantillas.length}`;
    if (plantillasFiltradas.length === 0) return "Sin resultados";
    if (plantillasFiltradas.length === plantillas.length) return `Mostrando todas (${plantillas.length})`;
    return `Mostrando ${plantillasFiltradas.length} de ${plantillas.length}`;
  }, [searchTerm, plantillas.length, plantillasFiltradas.length]);

  // ---------- helpers form
  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validatePlantilla = () => {
    const errors = {};
    if (!formData.nombre?.trim()) errors.nombre = "Nombre es requerido";
    if (!formData.tipo?.trim()) errors.tipo = "Tipo es requerido";

    const cuposNum = Number(formData.cupos);
    if (!Number.isFinite(cuposNum) || cuposNum < 1) errors.cupos = "Cupos debe ser mayor a 0";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ nombre: "", tipo: "", cupos: "10", activa: true });
    setFormErrors({});
    setHorarios([]);
    setNuevoHorario({ dia_semana: 0, hora: "" });
    formModal.open();
  };

  const openEdit = async (plantilla) => {
    setEditingId(plantilla.id);
    setFormData({
      nombre: plantilla.nombre || "",
      tipo: plantilla.tipo || "",
      cupos: String(plantilla.cupos ?? "10"),
      activa: plantilla.activa !== false,
    });
    setFormErrors({});
    setNuevoHorario({ dia_semana: 0, hora: "" });
    formModal.open();

    // cargar horarios
    setHorariosLoading(true);
    try {
      const data = await adminApi.getHorariosPlantillaAdmin(plantilla.id);
      if (data?.error) throw new Error(data.error);
      const lista = Array.isArray(data) ? data : [];
      setHorarios(
        lista
          .map((h) => ({ dia_semana: h.dia_semana, hora: normalizeHora(h.hora) }))
          .sort((a, b) => a.dia_semana - b.dia_semana || a.hora.localeCompare(b.hora))
      );
    } catch (e) {
      toast.error(e.message || "Error cargando horarios", { icon: "❌", duration: 5000 });
      setHorarios([]);
    } finally {
      setHorariosLoading(false);
    }
  };

  // ---------- horarios ui
  const addHorario = () => {
    const hora = normalizeHora(nuevoHorario.hora);
    if (!hora) {
      toast.error("Elegí una hora", { icon: "⚠️", duration: 2500 });
      return;
    }

    const exists = horarios.some(
      (h) => h.dia_semana === Number(nuevoHorario.dia_semana) && normalizeHora(h.hora) === hora
    );
    if (exists) {
      toast.error("Ese horario ya existe", { icon: "⚠️", duration: 2500 });
      return;
    }

    setHorarios((prev) =>
      [...prev, { dia_semana: Number(nuevoHorario.dia_semana), hora }]
        .sort((a, b) => a.dia_semana - b.dia_semana || a.hora.localeCompare(b.hora))
    );
    setNuevoHorario((p) => ({ ...p, hora: "" }));
  };

  const removeHorario = (index) => {
    setHorarios((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- submit plantilla + horarios (save)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePlantilla()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 3500 });
      return;
    }

    setSubmitting(true);
    const loadingToast = toast.loading(editingId ? "Guardando plantilla..." : "Creando plantilla...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const payload = {
        nombre: formData.nombre.trim(),
        tipo: formData.tipo.trim(),
        cupos: Number(formData.cupos),
        activa: Boolean(formData.activa),
      };

      let plantillaId = editingId;

      // 1) create/update plantilla
      if (!editingId) {
        const res = await adminApi.crearPlantillaAdmin( payload);
        if (res?.error) throw new Error(res.error);

        // puede venir {plantilla:{...}} por tu helper
        const created = res?.plantilla || res;
        plantillaId = created?.id;

        if (!plantillaId) {
          // fallback: recargar y buscar por nombre (último recurso)
          await reload();
          toast.success("Plantilla creada", { icon: "✅", duration: 2500 });
          formModal.close();
          return;
        }
      } else {
        const res = await adminApi.editarPlantillaAdmin( editingId, payload);
        if (res?.error) throw new Error(res.error);
      }

      // 2) guardar horarios (bulk replace)
      const resHor = await adminApi.guardarHorariosPlantillaAdmin( plantillaId, horarios);
      if (resHor?.error) throw new Error(resHor.error);

      toast.success("Plantilla guardada", { icon: "✅", duration: 2500 });
      formModal.close();
      await reload();
    } catch (err) {
      toast.error(err?.message || "Error al guardar plantilla", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setSubmitting(false);
    }
  };

  // ---------- delete plantilla
  const handleDelete = async (plantilla) => {
    const confirmed = await confirmAsync(
      `¿Eliminar la plantilla "${plantilla.nombre}"?\n\nSe eliminarán también sus horarios.`
    );
    if (!confirmed) return;

    const loadingToast = toast.loading("Eliminando plantilla...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const res = await adminApi.eliminarPlantillaAdmin( plantilla.id);
      if (res?.error) throw new Error(res.error);

      toast.success("Plantilla eliminada", { icon: "✅", duration: 2500 });
      await reload();
    } catch (err) {
      toast.error(err?.message || "Error al eliminar", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // ---------- generar clases
  const validateGenerar = () => {
    const errors = {};
    const semanasNum = Number(genData.semanas);
    if (!Number.isFinite(semanasNum) || semanasNum < 1) errors.semanas = "Semanas debe ser >= 1";
    setGenErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openGenerar = () => {
    setGenData({ semanas: "4", limpiar: false });
    setGenErrors({});
    generarModal.open();
  };

  const handleGenerar = async (e) => {
    e.preventDefault();
    if (!validateGenerar()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 3500 });
      return;
    }

    const loadingToast = toast.loading("Generando clases...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const res = await adminApi.generarClasesDesdePlantillasAdmin( {
        semanas: Number(genData.semanas),
        limpiar: Boolean(genData.limpiar),
      });

      if (res?.error) throw new Error(res.error);

      toast.success(`Listo: ${res.creadas ?? 0} creadas (${res.existentes ?? 0} ya existían)`, {
        icon: "✅",
        duration: 4000,
      });

      generarModal.close();
    } catch (err) {
      toast.error(err?.message || "Error generando clases", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // ---------- states
  if (loading && plantillas.length === 0) return <Loading primaryColor={primaryColor} />;
  if (error) return <ErrorMessage message={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <Card
        primaryColor={primaryColor}
        title="Plantillas"
        subtitle="Definí clases recurrentes con horarios y generá la agenda"
        actions={
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-slate-400">{resultsLabel}</span>
            <Button variant="ghost" onClick={openGenerar} className="text-sm whitespace-nowrap">
              Generar clases
            </Button>
            <Button variant="primary" onClick={openCreate} primaryColor={primaryColor}>
              Nueva plantilla
            </Button>
          </div>
        }
      >
        {/* Search */}
        <div className="mb-5">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <input
              type="text"
              placeholder="Buscar por nombre, tipo, horarios, cupos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-500
                         focus:ring-2 focus:border-transparent transition"
              style={{ "--tw-ring-color": primaryColor }}
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white"
                aria-label="Limpiar búsqueda"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-2 text-xs text-slate-400">
            {searchTerm.trim() ? (
              plantillasFiltradas.length === 0 ? (
                <span className="text-red-300">No se encontraron plantillas</span>
              ) : (
                <span>{resultsLabel}</span>
              )
            ) : (
              <span>{resultsLabel}</span>
            )}
          </div>
        </div>

        {/* List */}
        {plantillasFiltradas.length > 0 ? (
          <div className="space-y-4">
            {plantillasFiltradas.map((p) => (
              <PlantillaCard
                key={p.id}
                plantilla={p}
                primaryColor={primaryColor}
                onEditar={() => openEdit(p)}
                onEliminar={() => handleDelete(p)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-700 bg-slate-800/20 p-2">
            <EmptyState
              icon={searchTerm ? "🔍" : "📅"}
              title={searchTerm ? "No se encontraron plantillas" : "No hay plantillas configuradas"}
              message={
                searchTerm
                  ? `No hay plantillas que coincidan con "${searchTerm}"`
                  : "Creá una plantilla (con horarios) y después generá clases automáticamente."
              }
              primaryColor={primaryColor}
              action={
                !searchTerm && (
                  <Button variant="primary" onClick={openCreate} primaryColor={primaryColor}>
                    Crear primera plantilla
                  </Button>
                )
              }
            />
          </div>
        )}
      </Card>

      {/* Modal - Plantilla */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={editingId ? "Editar plantilla" : "Nueva plantilla"}
        primaryColor={primaryColor}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nombre"
            name="nombre"
            value={formData.nombre}
            onChange={(name, value) => handleInputChange(name, value)}
            error={formErrors.nombre}
            required
            placeholder="Ej: Spinning Matutino"
            primaryColor={primaryColor}
          />

          <Input
            label="Tipo"
            name="tipo"
            value={formData.tipo}
            onChange={(name, value) => handleInputChange(name, value)}
            error={formErrors.tipo}
            required
            placeholder="Ej: Spinning"
            primaryColor={primaryColor}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Cupos"
              name="cupos"
              type="number"
              value={formData.cupos}
              onChange={(name, value) => handleInputChange(name, value)}
              error={formErrors.cupos}
              required
              min="1"
              primaryColor={primaryColor}
            />

            <div className="rounded-xl border border-slate-700 bg-slate-800/20 p-3">
              <label className="block text-sm font-medium text-slate-200 mb-2">Estado</label>
              <button
                type="button"
                onClick={() => handleInputChange("activa", !formData.activa)}
                className="w-full inline-flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/40 px-4 py-3 text-sm text-white hover:bg-slate-900/60 transition"
              >
                <span>{formData.activa ? "Activa" : "Inactiva"}</span>
                <span style={{ color: primaryColor }} className="font-semibold">
                  {formData.activa ? "ON" : "OFF"}
                </span>
              </button>
            </div>
          </div>

          {/* Horarios */}
          <div className="rounded-2xl border border-slate-700 bg-slate-800/20 p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="text-white font-semibold">Horarios</h4>
              {horariosLoading ? (
                <span className="text-xs text-slate-400">Cargando...</span>
              ) : (
                <span className="text-xs text-slate-400">{horarios.length} configurado(s)</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-700 bg-slate-900/40 px-3 py-2">
                <label className="block text-xs text-slate-400 mb-1">Día</label>
                <select
                  value={nuevoHorario.dia_semana}
                  onChange={(e) => setNuevoHorario((p) => ({ ...p, dia_semana: Number(e.target.value) }))}
                  className="w-full bg-transparent text-white outline-none"
                >
                  {DIAS.map((d) => (
                    <option key={d.value} value={d.value} className="bg-slate-900">
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-900/40 px-3 py-2">
                <label className="block text-xs text-slate-400 mb-1">Hora</label>
                <input
                  type="time"
                  value={nuevoHorario.hora}
                  onChange={(e) => setNuevoHorario((p) => ({ ...p, hora: e.target.value }))}
                  className="w-full bg-transparent text-white outline-none"
                />
              </div>

              <div className="flex items-end">
                <Button variant="success" type="button" onClick={addHorario} className="w-full">
                  Agregar
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {horarios.length > 0 ? (
                horarios.map((h, idx) => (
                  <div
                    key={`${h.dia_semana}-${h.hora}-${idx}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-900/30 p-3"
                  >
                    <div className="text-sm text-white">
                      <span style={{ color: primaryColor }} className="font-semibold">
                        {DIAS.find((d) => d.value === h.dia_semana)?.label}
                      </span>{" "}
                      <span className="text-slate-300">• {normalizeHora(h.hora)}</span>
                    </div>

                    <Button variant="danger" type="button" onClick={() => removeHorario(idx)} className="text-xs">
                      Quitar
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400">No hay horarios todavía</p>
                  <p className="text-xs text-slate-500 mt-1">Agregá al menos uno para generar clases</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={formModal.close} type="button" disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal - Generar */}
      <Modal isOpen={generarModal.isOpen} onClose={generarModal.close} title="Generar clases" primaryColor={primaryColor}>
        <form onSubmit={handleGenerar} className="space-y-4">
          <Input
            label="Semanas a generar"
            name="semanas"
            type="number"
            value={genData.semanas}
            onChange={(name, value) => {
              setGenData((p) => ({ ...p, semanas: value }));
              if (genErrors.semanas) setGenErrors((p) => ({ ...p, semanas: null }));
            }}
            error={genErrors.semanas}
            required
            min="1"
            primaryColor={primaryColor}
          />

          <div className="rounded-xl border border-slate-700 bg-slate-800/20 p-3">
            <label className="flex items-center gap-3 text-sm text-white">
              <input
                type="checkbox"
                checked={genData.limpiar}
                onChange={(e) => setGenData((p) => ({ ...p, limpiar: e.target.checked }))}
              />
              Limpiar clases futuras generadas (dentro del rango) antes de generar
            </label>
            <p className="text-xs text-slate-400 mt-2">
              Recomendado si cambiaste horarios/cupos y querés regenerar la agenda.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={generarModal.close} type="button">
              Cancelar
            </Button>
            <Button variant="success" type="submit">
              Generar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function PlantillaCard({ plantilla, primaryColor, onEditar, onEliminar }) {
  const activa = plantilla.activa !== false;

  return (
    <Card primaryColor={primaryColor} className="hover:shadow-xl transition-all">
      <div className="flex items-start justify-between gap-4">
        {/* Info */}
        <div className="min-w-0 flex-1">
          {/* Nombre + badge en la misma fila, nombre trunca */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-lg font-bold text-white">{plantilla.nombre}</h3>
            <Badge variant={activa ? "success" : "danger"} className="shrink-0">
              {activa ? "Activa" : "Inactiva"}
            </Badge>
          </div>

          <div className="space-y-1 text-sm">
            <p style={{ color: primaryColor }} className="font-semibold">
              {plantilla.tipo} · {plantilla.cupos} cupos
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              {plantilla.descripcion_horarios || "Sin horarios configurados"}
            </p>
          </div>
        </div>

        {/* Botones — columna fija a la derecha */}
        <div className="flex flex-col gap-2 shrink-0">
          <Button variant="ghost" onClick={onEditar} className="text-sm whitespace-nowrap">
            Editar
          </Button>
          <button
            onClick={onEliminar}
            className="inline-flex items-center justify-center rounded-xl border border-red-500/30 bg-red-600/10 px-3 py-2 text-sm font-medium text-red-300 transition hover:bg-red-600/20 hover:text-red-200 focus:outline-none whitespace-nowrap"
          >
            Eliminar
          </button>
        </div>
      </div>
    </Card>
  );
}
